import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const EMPTY = { title: "", category: "", industry: "", description: "", requirements: "", budget: "", currency: "INR", country: "", city: "", source: "",
  credit_cost: 5, verification_status: "pending", client_name: "", phone: "", email: "", whatsapp: "" };

const F = ({ k, label, f, set, type = "text", span, required }) => (
  <div className={span ? "sm:col-span-2" : ""}>
    <Label htmlFor={`lf-${k}`}>{label}{required && " *"}</Label>
    <Input id={`lf-${k}`} type={type} required={required} className="mt-1.5" data-testid={`lead-form-${k.replace("_", "-")}`} value={f[k]} onChange={(e) => set(k, e.target.value)} />
  </div>
);

export default function LeadFormDialog({ open, lead, onOpenChange, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (open) setF(lead ? { ...EMPTY, ...lead, ...(lead.contact || {}) } : EMPTY);
  }, [open, lead]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k] ?? ""]));
    payload.budget = Number(payload.budget || 0);
    payload.credit_cost = Number(payload.credit_cost || 1);
    try {
      if (lead) await api.put(`/admin/leads/${lead.id}`, payload);
      else await api.post("/admin/leads", payload);
      toast.success(lead ? "Lead updated" : "Lead created");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="lead-form-dialog">
        <DialogHeader><DialogTitle className="font-display">{lead ? "Edit lead" : "Add new lead"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-6">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-primary mb-3">Public project details</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <F k="title" label="Title" f={f} set={set} span required />
              <F k="category" label="Category" f={f} set={set} required />
              <F k="industry" label="Industry" f={f} set={set} />
              <div className="sm:col-span-2"><Label>Description *</Label><Textarea required rows={3} className="mt-1.5" data-testid="lead-form-description" value={f.description} onChange={(e) => set("description", e.target.value)} /></div>
              <div className="sm:col-span-2"><Label>Requirements</Label><Textarea rows={2} className="mt-1.5" data-testid="lead-form-requirements" value={f.requirements} onChange={(e) => set("requirements", e.target.value)} /></div>
              <F k="budget" label="Budget" f={f} set={set} type="number" required />
              <F k="currency" label="Currency" f={f} set={set} />
              <F k="country" label="Country" f={f} set={set} required />
              <F k="city" label="City" f={f} set={set} />
              <F k="credit_cost" label="Credit cost" f={f} set={set} type="number" required />
              <div>
                <Label>Verification status</Label>
                <Select value={f.verification_status} onValueChange={(v) => set("verification_status", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="lead-form-verification-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="verified">Verified</SelectItem><SelectItem value="rejected">Rejected</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-amber-700 dark:text-amber-400 mb-3">Private contact (stored separately)</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <F k="client_name" label="Client name" f={f} set={set} required />
              <F k="email" label="Email" f={f} set={set} type="email" />
              <F k="phone" label="Phone" f={f} set={set} />
              <F k="whatsapp" label="WhatsApp" f={f} set={set} />
              <F k="source" label="Source" f={f} set={set} span />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="lead-form-submit">{lead ? "Save changes" : "Create lead"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
